import { Entity, Field, getEntityType } from "@the-real-insight/tri-model";
import i18next from "i18next";

interface Properties {
    entity: Entity;
    hiddenFields?: string[];
    valueWidth?: string;
}

export const EntityFields = (properties: Properties) => {
    console.log(properties.entity);

    if (!properties.entity) return <></>;

    const lang = i18next.resolvedLanguage || 'de';

    // A value that is a plain object — or a string holding a JSON object — is
    // shown as a nested key/value list instead of a raw JSON blob (e.g. an
    // asset's `properties` / "Eigenschaften" map).
    const asPlainObject = (value: any): Record<string, any> | null => {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            return value as Record<string, any>;
        }
        if (typeof value === 'string') {
            const s = value.trim();
            if (s.startsWith('{') && s.endsWith('}')) {
                try {
                    const parsed = JSON.parse(s);
                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                        return parsed;
                    }
                } catch (e) {
                    // not JSON — fall through and render as text
                }
            }
        }
        return null;
    };

    const renderFlatObject = (obj: Record<string, any>) => {
        const entries = Object.entries(obj);
        if (entries.length === 0) return '-';
        return (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                    {entries.map(([key, value]) => (
                        <tr key={key}>
                            <td style={{ verticalAlign: 'top', paddingRight: '8px', color: '#666', whiteSpace: 'nowrap' }}>{key}:</td>
                            <td style={{ wordBreak: 'break-word' }}>{renderValue(value)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    const renderValue = (value: any): any => {
        if (value === undefined || value === null || value === '') return '-';
        const obj = asPlainObject(value);
        if (obj) return renderFlatObject(obj);
        if (Array.isArray(value)) {
            if (value.length === 0) return '-';
            return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
        }
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch (e) {
                return '[Object]';
            }
        }
        return String(value);
    };

    const renderRows = () => {
        let typeClass: any = null;
        try {
            if (properties.entity && typeof (properties.entity as any).getTypeClass === 'function') {
                typeClass = (properties.entity as any).getTypeClass();
            } else if (properties.entity && (properties.entity as any)._type) {
                typeClass = getEntityType((properties.entity as any)._type);
            }
        } catch (e) {
            // Ignore errors from getEntityType if type is not registered
        }

        const labelStyle = { verticalAlign: 'top', paddingRight: '10px', width: '35%', minWidth: '100px' } as const;
        const valueStyle = { width: '65%', wordBreak: 'break-word', whiteSpace: 'pre-wrap' } as const;

        const isEmptyValue = (value: any) =>
            value === undefined || value === null || value === '' ||
            (Array.isArray(value) && value.length === 0);

        // A field holding a JSON map (e.g. an asset's `properties` / "Eigenschaften") is dissolved
        // into the flow: each entry becomes its own attribute row, without a wrapper label. Empty
        // container fields (JSON-typed, or the free-form `properties`/`details`) are dropped entirely
        // rather than shown as "Details: -".
        const containerFields = new Set(['properties', 'details']);

        if (typeClass && typeClass.fields) {
            const rows: any[] = [];
            typeClass.fields
                // `internal` covers what belongs to the record rather than to the subject —
                // the change log above all, which as a row of raw JSON tells a reader nothing.
                .filter(
                    (field: Field) =>
                        !field.internal &&
                        field.name !== '_id' &&
                        field.name !== '_tenant' &&
                        !properties.hiddenFields?.includes(field.name),
                )
                .forEach((field: Field) => {
                    const value = (properties.entity as any)[field.name];
                    const shouldDissolve = field.type === JSON || containerFields.has(field.name);
                    const obj = shouldDissolve ? asPlainObject(value) : null;
                    if (obj) {
                        Object.entries(obj).forEach(([key, entryValue]) => {
                            rows.push(
                                <tr key={`${field.name}.${key}`}>
                                    <td className="fontWeightBold" style={labelStyle}>{key}:</td>
                                    <td style={valueStyle}>{renderValue(entryValue)}</td>
                                </tr>
                            );
                        });
                        return;
                    }
                    if (isEmptyValue(value) && shouldDissolve) {
                        return;
                    }
                    rows.push(
                        <tr key={field.name}>
                            <td className="fontWeightBold" style={labelStyle}>{field.getLabel(lang)}:</td>
                            <td style={valueStyle}>
                                {field.type === Date
                                    ? (() => {
                                        if (!value) return '-';
                                        const date = new Date(value);
                                        return isNaN(date.getTime()) ? '-' : date.toLocaleDateString(lang);
                                    })()
                                    : renderValue(value)}
                            </td>
                        </tr>
                    );
                });
            return rows;
        } else {
            // Fallback for plain objects: show all non-metadata properties
            const rows: any[] = [];
            Object.entries(properties.entity)
                .filter(([key, value]) => {
                    const isInternal = key.startsWith('_') || typeof value === 'function' ||
                                     ['class', 'entityType', 'getTypeName', 'display', 'entity'].includes(key);
                    const isHidden = properties.hiddenFields?.includes(key);
                    return !isInternal && !isHidden;
                })
                .forEach(([key, value]) => {
                    const shouldDissolve = containerFields.has(key);
                    const obj = shouldDissolve ? asPlainObject(value) : null;
                    if (obj) {
                        Object.entries(obj).forEach(([subKey, subValue]) => {
                            rows.push(
                                <tr key={`${key}.${subKey}`}>
                                    <td className="fontWeightBold" style={labelStyle}>{subKey}:</td>
                                    <td style={valueStyle}>{renderValue(subValue)}</td>
                                </tr>
                            );
                        });
                        return;
                    }
                    if (isEmptyValue(value) && shouldDissolve) {
                        return;
                    }
                    rows.push(
                        <tr key={key}>
                            <td className="fontWeightBold" style={labelStyle}>{key}:</td>
                            <td style={valueStyle}>{renderValue(value)}</td>
                        </tr>
                    );
                });
            return rows;
        }
    };

    return <table className="width100Pct">
        <tbody>
            {renderRows()}
        </tbody>
    </table>;
}
